/**
 * ============================================================================
 * LEGACY-URL BACKWARD-COMPAT SHIM — DEV-3454 — remove after 2027-01-01
 * ============================================================================
 * Old links carry a `/mc/<cat>/e/<id>` path tail that was dropped from generated
 * URLs (mc removed, selected element moved to `?selected=`). Until such links
 * stop circulating we keep matching them. When removing the shim, delete in
 * exactly these places — nothing else depends on the legacy tail:
 *
 *   1. The `:mc?/:mainCategoryId?/:e?/:elementId?` tail in the three
 *      `*_MATCH_PATH` constants below (then point `<Route>`/`useMatch`/
 *      `matchPath` back at the canonical `NODON_*_PATH` constants and delete
 *      the `*_MATCH_PATH` constants).
 *   2. The legacy `/e/<id>` fallback in `selectionParamsFromRoute`
 *      (client/src/hooks/use-router.hooks.tsx) — grep "DEV-3454".
 *
 * The canonicalizing reroute in DataLoaderProxy needs no change: it always
 * rebuilds the short URL and will simply stop having legacy input to upgrade.
 * ============================================================================
 */

// Loose matcher for authenticated project routes. Legacy-tolerant (see shim
// note above). Used only for `matchPath`/`useMatch`/`<Route>` — never to generate URLs.
export const NODON_PROJECT_PATH =
  '/:organization?/:projects?/:projectId?/:v?/:versionId?/:page?/:mc?/:mainCategoryId?/:e?/:elementId?';

/**
 * Legacy-tolerant matchers for public routes (see shim note above). Same as
 * their canonical siblings but also match the old `/mc/<cat>/e/<id>` tail.
 * Used only for `matchPath`/`useMatch`/`<Route>`.
 */
export const NODON_SHARED_PROJECT_MATCH_PATH =
  '/shared/:sharingKey/v/:versionId/:page?/:mc?/:mainCategoryId?/:e?/:elementId?';
export const NODON_GENERATION_PROJECT_MATCH_PATH =
  '/generation/:generationKey/v/:versionId/:page?/:mc?/:mainCategoryId?/:e?/:elementId?';

// Canonical generated path — selected element lives in `?selected=`, not the path.
export const NODON_PROJECT_PATH_STRICT =
  '/:organization?/projects/:projectId?/v/:versionId?/:page?';

/** Canonical generated shared-project path. */
export const NODON_SHARED_PROJECT_PATH =
  '/shared/:sharingKey/v/:versionId/:page?';

/** Internal one-time PDF render route; not shown to users. */
export const NODON_GENERATION_PROJECT_PATH =
  '/generation/:generationKey/v/:versionId/:page?';

export const NODON_PARTIAL_PROJECT_ORG_PATH = '/:organization';
export const NODON_PARTIAL_PROJECT_PATH = `${NODON_PARTIAL_PROJECT_ORG_PATH}/projects/:projectId`;
export const NODON_PROJECTS_LIST_PATH = '/:organization/projects';

export const NODON_NOT_FOUND = '/not-found';

/** First URL segments for public project routes — never valid organization names. */
export const RESERVED_PROJECT_ROUTE_PREFIXES = [
  'generation',
  'shared',
] as const;

/** True when a path segment is a public route prefix, not an organization slug. */
export const isReservedProjectRoutePrefix = (
  segment: string | undefined,
): boolean =>
  segment != null &&
  (RESERVED_PROJECT_ROUTE_PREFIXES as readonly string[]).includes(segment);

/** True when the Organization header names a public project context (shared link or PDF generation) instead of a real organization. */
export const isPublicProjectOrganization = (organization: string): boolean =>
  isReservedProjectRoutePrefix(organization);

/** True for `/generation/...` or full shared project URLs (`/shared/.../v/...`). */
export const isPublicProjectPathname = (pathname: string): boolean =>
  pathname.startsWith('/generation') ||
  (pathname.startsWith('/shared') && pathname.includes('/v/'));
